import { Pipe, PipeTransform } from '@angular/core';

import { MediaTypeEnum } from '../../../Basic/enums/media-type.enum';


/**
 * This pipe adds links to mention body contents
 * If the mention media type is social, then also turns hashtags into links
 */
@Pipe({
    name: 'mentionLinks',
    standalone: false
})
export class MentionLinksPipe implements PipeTransform {
    static readonly urlRegex = /(^|\W)(https?:\/\/[^\s]+)/g;
    static readonly urlReplaceString = '$1<a target="_blank" href="$2">$2</a>';
    static readonly highlightMarking = /agprhighlight(filter)?(social)?(start)?(end)?/g;
    static readonly hashtagRegex = /(^|\W)#([^\s]+)(?=\W|$)/g;
    static readonly hashtagReplaceStrings = {
        [MediaTypeEnum.FACEBOOK]: '$1<a target="_blank" href="https://www.facebook.com/hashtag/$2">#$2</a>',
        [MediaTypeEnum.TWITTER]: '$1<a target="_blank" href="https://twitter.com/hashtag/$2">#$2</a>',
        [MediaTypeEnum.TIKTOK]: '$1<a target="_blank" href="https://tiktok.com/tag/$2">#$2</a>',
        [MediaTypeEnum.INSTAGRAM]: '$1<a target="_blank" href="https://www.instagram.com/explore/tags/$2/">#$2</a>',
        [MediaTypeEnum.YOUTUBE]: '$1<a target="_blank" href="https://www.youtube.com/results?search_query=%23$2">#$2</a>',
        [MediaTypeEnum.REDDIT]: '$1<a target="_blank" href="https://www.reddit.com/r/$2">#$2</a>',
        [MediaTypeEnum.LINKEDIN]: '$1<a target="_blank" href="https://www.linkedin.com/feed/hashtag/$2">#$2</a>',
        [MediaTypeEnum.BLUESKY]: '$1<a target="_blank" href="https://bsky.app/hashtag/$2">#$2</a>',
    };

    /** Generic handle, starts with @, allows letters, numbers, and underscores */
    static readonly handleRegex = /(^|\W)@(\w+)(?=\W|$)/g;

    /** Youtube handle, starts with @, allows letters, numbers, underscores, and hyphens */
    static readonly youtubeHandleRegex = /(^|\W)@([\w-]+)(?=\W|$)/g;

    /** Reddit handle, starts with u/, allows letters, numbers, underscores, and hyphens */
    static readonly redditHandleRegex = /(^|\W)u\/([\w-]+)(?=\W|$)/g;

    /** Bluesky handle, start with @, allows letters, numbers, dots and hyphens */
    static readonly blueskyHandleRegex = /(^|\W)@([a-zA-Z0-9.-]+)(?=\W|$)/g;


    static readonly handlesReplaceStrings = {
        [MediaTypeEnum.FACEBOOK]: '$1<a target="_blank" href="https://www.facebook.com/$2">@$2</a>',
        [MediaTypeEnum.TWITTER]: '$1<a target="_blank" href="https://twitter.com/$2">@$2</a>',
        // for now we don't want to show the tiktok handles as links, because it's displaying the username so it's
        // wrong & not the handle so it's giving the wrong url
        // [MediaTypeEnum.TIKTOK]: '$1<a target="_blank" href="https://tiktok.com/@$2">@$2</a>',
        [MediaTypeEnum.INSTAGRAM]: '$1<a target="_blank" href="https://www.instagram.com/$2/">@$2</a>',
        [MediaTypeEnum.YOUTUBE]: '$1<a target="_blank" href="https://www.youtube.com/@$2">@$2</a>',
        [MediaTypeEnum.REDDIT]: '$1<a target="_blank" href="https://www.reddit.com/user/$2/">u/$2</a>',
        [MediaTypeEnum.BLUESKY]: '$1<a target="_blank" href="https://bsky.app/profile/$2/">@$2</a>'
    };

    /** Turns any detected urls or hashtags in content into links */
    transform(text: string, mediaType: MediaTypeEnum): string {
        if (!text) {
            return '';
        }
        let html = text;

        html = this.addLinks(html);
        html = this.addHashtagLinks(html, mediaType);
        html = this.addHandleLinks(html, mediaType);

        return html;
    }

    /** Detects urls in contents, and turns it into a link */
    private addLinks(html: string): string {
        return html.replace(MentionLinksPipe.urlRegex, (match, $1, $2) => {
            const replaceString = MentionLinksPipe.urlReplaceString;
            if (match.match(MentionLinksPipe.highlightMarking)){
                return this.processHighlightMarking(replaceString, $1, $2);
            } else {
                return match.replace(MentionLinksPipe.urlRegex, replaceString);
            }
        });
    }

    /** Detects hastags in contents, and turns it into a link based on the social media type */
    private addHashtagLinks(html: string, mediaType: MediaTypeEnum): string {
        const replaceString = MentionLinksPipe.hashtagReplaceStrings[mediaType];

        if (!replaceString) {
            return html;
        }

        return html.replace(MentionLinksPipe.hashtagRegex, (match, $1, $2) => {
            if (match.match(MentionLinksPipe.highlightMarking)){
                return this.processHighlightMarking(replaceString, $1, $2);
            } else {
                return match.replace(MentionLinksPipe.hashtagRegex, replaceString);
            }
        });
    }


    /** Detects handles in contents, and turns it into a link based on the social media type */
    private addHandleLinks(html: string, mediaType: MediaTypeEnum): string {
        const replaceString = MentionLinksPipe.handlesReplaceStrings[mediaType];

        if (!replaceString) {
            return html;
        }

        let handleR: RegExp;
        switch(mediaType) {
            case MediaTypeEnum.REDDIT:
                handleR = MentionLinksPipe.redditHandleRegex;
                break;
            case MediaTypeEnum.YOUTUBE:
                handleR = MentionLinksPipe.youtubeHandleRegex;
                break;
            case MediaTypeEnum.BLUESKY:
                handleR = MentionLinksPipe.blueskyHandleRegex;
                break;
            default:
                handleR = MentionLinksPipe.handleRegex;
        }

        return html.replace(handleR, (match, $1, $2) => {
            if (match.match(MentionLinksPipe.highlightMarking)){
                return this.processHighlightMarking(replaceString, $1, $2);
            } else {
                return match.replace(handleR, replaceString);
            }
        });
    }

    /**
     * Substitute the $1 and $2 in the replaceString
     * @param replaceString the string to be substituted, containts $1 and $2 as marking for substitute
     * @param $1 the first capturing group by the outer regex function
     * @param $2 the second capturing group by the outer regex function usually containts the marking for highlight(i.e. agprhighlight)
     * @returns string after substitution
     */
    private processHighlightMarking(replaceString: string, $1: any, $2: any): string{
        const highlightText = $2.replace(MentionLinksPipe.highlightMarking, '');
        let replaced = replaceString.replace('$1', $1);
        replaced = replaced.replace(/\$2(?=[^<])/g, highlightText); // replace the one in the href
        replaced = replaced.replace(/\$2/g, $2); // replace the one inside <a>
        return replaced;
    }
}
